import {
  CONVERSATION_DECK_SERVER_CATEGORIES,
  isPlusDeckCategory,
  type ConversationDeckBootstrapResponse,
  type ConversationDeckCategory,
  type ConversationDeckHelper,
  type ConversationDeckRefillResponse,
  type ConversationDeckServerCategory,
} from "../types";

export const CONVERSATION_DECK_BANK_VERSION = 17;
export const CONVERSATION_DECK_CACHE_SCHEMA_VERSION = 1;
export const CONVERSATION_DECK_MAX_CACHE_SIZE = 160;
export const CONVERSATION_DECK_CATEGORY_TARGET = 20;
export const CONVERSATION_DECK_REFILL_THRESHOLD = 8;
export const CONVERSATION_DECK_RECENT_LIMIT = 80;

export type CachedConversationDeckHelper = ConversationDeckHelper & {
  consumed: boolean;
};

export type ConversationDeckCacheState = {
  ownerUserId: string;
  bankVersion: number;
  schemaVersion: number;
  revision: number;
  helpers: CachedConversationDeckHelper[];
  recentIds: string[];
  /**
   * Whether this cache was filled by an Opncue Plus account.
   *
   * The deck is cached on the device, and the server sends a free account
   * four decks and a Plus account seven. So the cache is only usable by the
   * plan that built it: upgrading has to re-bootstrap or the three paid decks
   * would stay empty behind an unlocked pill, and downgrading has to
   * re-bootstrap or the paid helpers would linger on the device after the
   * subscription ended.
   */
  plus: boolean;
};

export type ConversationDeckSelection = {
  helper: ConversationDeckHelper;
  refillCategory: ConversationDeckServerCategory | null;
  unusedCount: number;
};

/** The categories a bootstrap for this plan must contain, and only these. */
export function expectedBootstrapCategories(
  plus: boolean,
): readonly ConversationDeckServerCategory[] {
  return plus
    ? CONVERSATION_DECK_SERVER_CATEGORIES
    : CONVERSATION_DECK_SERVER_CATEGORIES.filter(
        (category) => !isPlusDeckCategory(category),
      );
}

/**
 * `plus` is required, and it is the whole point of this function.
 *
 * It used to demand all seven categories unconditionally. The server sends a
 * free account only the four free decks — deliberately, because bootstrap ships
 * the deck to the device and a client-side paywall would be a drawing of one —
 * so every free bootstrap failed on `go_deeper`, the cache was never written,
 * and free users got no deck AT ALL. Not the paid three: all of it. The check
 * read as strict, and strictness against the wrong expectation is just a bug
 * that looks careful.
 *
 * It still rejects a SHORT deck, which is what it was written for, and it now
 * also rejects a deck carrying a category this plan should never receive — a
 * free device holding paid helpers is the leak the server-side filter exists to
 * prevent, so the client should refuse to cache it rather than quietly enjoy it.
 */
export function validateBootstrapResponse(
  value: ConversationDeckBootstrapResponse,
  plus: boolean,
): ConversationDeckBootstrapResponse {
  if (
    value.bankVersion !== CONVERSATION_DECK_BANK_VERSION ||
    value.schemaVersion !== CONVERSATION_DECK_CACHE_SCHEMA_VERSION ||
    value.invalidated !== false ||
    !Number.isInteger(value.revision) ||
    value.revision < 1 ||
    !Array.isArray(value.helpers) ||
    value.helpers.length > CONVERSATION_DECK_MAX_CACHE_SIZE
  ) {
    throw new Error("Invalid Conversation Deck bootstrap response.");
  }
  validateHelpers(value.helpers);

  const expected = expectedBootstrapCategories(plus);
  for (const category of expected) {
    const count = value.helpers.filter(
      (helper) => helper.category === category,
    ).length;
    if (count !== CONVERSATION_DECK_CATEGORY_TARGET) {
      throw new Error(
        `Conversation Deck bootstrap is incomplete for ${category}.`,
      );
    }
  }

  const unexpected = value.helpers.find(
    (helper) =>
      !(expected as readonly string[]).includes(helper.category),
  );
  if (unexpected) {
    throw new Error(
      `Conversation Deck bootstrap carried ${unexpected.category}, which this plan may not have.`,
    );
  }

  return value;
}

export function validateRefillResponse(
  value: ConversationDeckRefillResponse,
): ConversationDeckRefillResponse {
  if (
    value.bankVersion !== CONVERSATION_DECK_BANK_VERSION ||
    value.schemaVersion !== CONVERSATION_DECK_CACHE_SCHEMA_VERSION ||
    value.invalidated !== false ||
    !Number.isInteger(value.revision) ||
    value.revision < 1 ||
    !CONVERSATION_DECK_SERVER_CATEGORIES.includes(value.category) ||
    !Array.isArray(value.helpers) ||
    value.helpers.length < 1 ||
    value.helpers.length > 20 ||
    value.helpers.some((helper) => helper.category !== value.category)
  ) {
    throw new Error("Invalid Conversation Deck refill response.");
  }
  validateHelpers(value.helpers);
  return value;
}

export function createConversationDeckCache(
  ownerUserId: string,
  response: ConversationDeckBootstrapResponse,
  plus: boolean,
): ConversationDeckCacheState {
  const valid = validateBootstrapResponse(response, plus);
  return {
    ownerUserId,
    bankVersion: valid.bankVersion,
    schemaVersion: valid.schemaVersion,
    revision: valid.revision,
    helpers: valid.helpers.map((helper) => ({ ...helper, consumed: false })),
    recentIds: [],
    plus,
  };
}

export function isConversationDeckCacheValid(
  state: ConversationDeckCacheState | null,
  ownerUserId: string,
  plus: boolean,
): state is ConversationDeckCacheState {
  if (
    !state ||
    state.ownerUserId !== ownerUserId ||
    // A cache is only usable by the plan that built it. Upgrading has to
    // re-bootstrap or the three paid decks stay empty behind an unlocked
    // pill; lapsing has to re-bootstrap or the paid helpers linger on the
    // device after the subscription ended.
    state.plus !== plus ||
    state.bankVersion !== CONVERSATION_DECK_BANK_VERSION ||
    state.schemaVersion !== CONVERSATION_DECK_CACHE_SCHEMA_VERSION ||
    state.helpers.length > CONVERSATION_DECK_MAX_CACHE_SIZE ||
    state.recentIds.length > CONVERSATION_DECK_RECENT_LIMIT
  ) {
    return false;
  }
  try {
    validateHelpers(state.helpers);
    return true;
  } catch {
    return false;
  }
}

export function takeConversationDeckHelper(
  state: ConversationDeckCacheState,
  category: ConversationDeckCategory,
): {
  state: ConversationDeckCacheState;
  selection: ConversationDeckSelection | null;
} {
  const available = state.helpers.filter(
    (helper) => !helper.consumed && helper.category === category,
  );
  if (!available.length) return { state, selection: null };

  const helper =
    available.find((candidate) => !state.recentIds.includes(candidate.id)) ??
    available[0];
  const helpers = state.helpers.map((candidate) =>
    candidate.id === helper.id ? { ...candidate, consumed: true } : candidate,
  );
  const recentIds = [...state.recentIds.filter((id) => id !== helper.id), helper.id]
    .slice(-CONVERSATION_DECK_RECENT_LIMIT);
  const unusedCount = helpers.filter(
    (candidate) =>
      candidate.category === helper.category && !candidate.consumed,
  ).length;

  return {
    state: { ...state, helpers, recentIds },
    selection: {
      helper: stripCacheFields(helper),
      refillCategory:
        unusedCount < CONVERSATION_DECK_REFILL_THRESHOLD
          ? helper.category
          : null,
      unusedCount,
    },
  };
}

export function applyConversationDeckRefill(
  state: ConversationDeckCacheState,
  response: ConversationDeckRefillResponse,
): ConversationDeckCacheState {
  const valid = validateRefillResponse(response);
  const existingIds = new Set(state.helpers.map((helper) => helper.id));
  const incoming = valid.helpers.filter((helper) => !existingIds.has(helper.id));
  if (!incoming.length) {
    return { ...state, revision: Math.max(state.revision, valid.revision) };
  }

  const removableIds = new Set(
    state.helpers
      .filter(
        (helper) => helper.category === valid.category && helper.consumed,
      )
      .slice(0, incoming.length)
      .map((helper) => helper.id),
  );
  const remaining = state.helpers.filter(
    (helper) => !removableIds.has(helper.id),
  );
  const categoryCount = remaining.filter(
    (helper) => helper.category === valid.category,
  ).length;
  const capacity = Math.max(
    0,
    CONVERSATION_DECK_CATEGORY_TARGET - categoryCount,
  );
  const additions = incoming
    .slice(0, capacity)
    .map((helper) => ({ ...helper, consumed: false }));
  const helpers = [...remaining, ...additions];
  if (helpers.length > CONVERSATION_DECK_MAX_CACHE_SIZE) {
    throw new Error("Conversation Deck cache cap exceeded.");
  }
  return {
    ...state,
    revision: Math.max(state.revision, valid.revision),
    helpers,
  };
}

export function unusedConversationDeckCount(
  state: ConversationDeckCacheState,
  category: ConversationDeckServerCategory,
): number {
  return state.helpers.filter(
    (helper) => helper.category === category && !helper.consumed,
  ).length;
}

function validateHelpers(
  helpers: (ConversationDeckHelper | CachedConversationDeckHelper)[],
): void {
  const ids = new Set<string>();
  for (const helper of helpers) {
    if (
      !helper ||
      typeof helper.id !== "string" ||
      !helper.id ||
      ids.has(helper.id) ||
      !CONVERSATION_DECK_SERVER_CATEGORIES.includes(helper.category) ||
      typeof helper.main !== "string" ||
      !helper.main.trim() ||
      typeof helper.possibleFollowUp !== "string" ||
      !helper.possibleFollowUp.trim() ||
      helper.main.includes("—") ||
      helper.possibleFollowUp.includes("—")
    ) {
      throw new Error("Conversation Deck helper payload is invalid.");
    }
    ids.add(helper.id);
  }
}

function stripCacheFields(
  helper: CachedConversationDeckHelper,
): ConversationDeckHelper {
  return {
    id: helper.id,
    category: helper.category,
    main: helper.main,
    possibleFollowUp: helper.possibleFollowUp,
  };
}
